import { routes } from '@/config/routes'
import { useMixpanel } from '@/context/mixpanel'
import type { PositionedVault, PositionedVaultsListElement } from '@/hooks/useVaultsWithPositions'
import type { VaultApyRange } from '@/types/apy'
import { formatApyTimestamp, getApyRangeCopy, getApyValue, getCappedApyCopy } from '@/utils/apy'
import { cn } from '@/utils/cn'
import { getCuratorDisplayName } from '@/utils/curator'
import { getNetworkName } from '@/utils/network/getNetworkName'
import { getProtocolDisplayName } from '@/utils/protocol'
import { getValueColor } from '@/utils/styles/getValueColor'
import { formatUSD } from '@vaultsfyi/common/utils'
import { Link, generatePath } from 'react-router'
import { ApyTooltip } from '../common/apyTooltip'
import { Button } from '../common/button'
import { NetworkImage } from '../common/networkImage'
import { ProtocolImage } from '../common/protocolImage'
import { Separator } from '../common/separator'
import { TokenImage } from '../common/tokenImage'
import { Tooltip, TooltipContent } from '../common/tooltip'
import { CategoryBadge } from './categoryBadge'
import { CuratorInfo } from './curatorInfo'
import { PositionBadge } from './positionBadge'

interface Props {
  vault: PositionedVault | PositionedVaultsListElement
  apyRange: VaultApyRange
  includeRewards: boolean
  className?: string
}

export const VaultCard = ({ vault, apyRange, includeRewards, className }: Props) => {
  const {
    name,
    protocol,
    curator,
    asset,
    network,
    tvl,
    tags,
    address,
    balanceUsd,
    apy,
    rewards,
    additionalIncentives,
    apyComposite,
    lastUpdateTimestamp,
  } = vault
  const { trackEvent } = useMixpanel()
  const protocolDisplayName = getProtocolDisplayName(protocol)
  const curatorDisplayName = curator ? getCuratorDisplayName(curator) : undefined

  return (
    <article
      className={cn(
        'group relative flex w-full flex-col gap-2 overflow-hidden rounded-2xl border bg-surface p-3',
        'ring-inset has-[:focus-visible]:ring',
        className
      )}
    >
      <header className="mb-auto">
        <div className="flex min-w-0 max-w-full items-center gap-2 overflow-hidden">
          <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden" title={name}>
            <Tooltip tabIndex={-1} className="relative z-[1] max-w-full shrink-0 overflow-hidden">
              <ProtocolImage protocol={protocol} className="size-7 p-0.5 group-even:bg-page" />
              <TooltipContent>
                <div className="flex items-center gap-1">
                  {protocol.protocolUrl ? (
                    <Button
                      variant="default"
                      size="default"
                      className="inline-flex items-center gap-1 rounded-full text-base underline"
                      asChild
                    >
                      <a href={protocol.protocolUrl} target="_blank" rel="noreferrer">
                        <ProtocolImage protocol={protocol} className="size-6" />
                        <span>{protocolDisplayName}</span>
                      </a>
                    </Button>
                  ) : (
                    <div className="inline-flex items-center gap-1">
                      <ProtocolImage protocol={protocol} className="size-6" />
                      <span className="text-base">{protocolDisplayName}</span>
                    </div>
                  )}
                </div>
              </TooltipContent>
            </Tooltip>
            <Button
              variant="default"
              size="default"
              className={cn(
                '!ring-0 min-w-0 text-heading-foreground',
                'before:absolute before:inset-0 before:h-full before:w-full'
              )}
              asChild
            >
              <Link
                to={generatePath(routes.opportunity, { vaultId: vault.vaultId, network: network.name })}
                prefetch="viewport"
                onClick={() => trackEvent('Vault card clicked', { Vault: name, Network: network, Address: address })}
              >
                <h3 title={name} className="max-w-full overflow-hidden text-ellipsis text-nowrap font-semibold">
                  <span className="sr-only">Go to</span> {name} <span className="sr-only">vault page</span>
                </h3>
              </Link>
            </Button>
          </div>
          <PositionBadge balanceUsd={balanceUsd} />
        </div>
        {tags.length > 0 && (
          <div className="mt-2">
            {tags.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5">
                {tags.map((tag) => (
                  <CategoryBadge category={tag} key={tag} size="sm" />
                ))}
              </div>
            )}
          </div>
        )}
        <p className="mt-2 flex flex-wrap items-center gap-1 text-xs">
          <span className="flex items-center gap-1">
            Supply
            <span className="flex items-center gap-1">
              <TokenImage symbol={asset.symbol} className="size-4" />
              <span>{asset.symbol}</span>
            </span>
          </span>
          <span className="flex items-center gap-1">
            on
            <span className="flex items-center gap-1">
              <NetworkImage network={network.name} className="size-4" />
              <span className="capitalize">{getNetworkName(network.name)}</span>
            </span>
          </span>
        </p>
        {curator && (
          <div className="mt-2 flex flex-wrap items-center gap-1 text-xs">
            <p>
              Curated by <span className="sr-only">{curatorDisplayName}</span>
            </p>
            <CuratorInfo curator={curator} size="inherit" className="gap-x-1.5" />
          </div>
        )}
      </header>
      <Separator />
      <section className="flex justify-between gap-3">
        <div className="flex flex-col gap-1 font-medium">
          <span className="text-xs">TVL</span>
          <p className="text-heading-foreground">{formatUSD(tvl.usd)}</p>
        </div>
        <div className="flex flex-col items-end gap-1 text-right font-medium">
          <span className="text-xs">Yield ({getApyRangeCopy(apyRange).compact})</span>
          <div className="flex items-center gap-1">
            <ApyTooltip
              tabIndex={-1}
              asset={asset}
              additionalIncentives={additionalIncentives}
              apy={apy}
              apyRange={apyRange}
              rewards={rewards}
              apyComposite={apyComposite}
              includeRewards={includeRewards}
              className="relative z-[1]"
            />
            <p className={cn(getValueColor(getApyValue({ apy, apyRange, includeRewards, apyComposite })))}>
              {getCappedApyCopy({ apy, apyRange, includeRewards, apyComposite })}
              <span className="hidden">(Yield last updated: {formatApyTimestamp(lastUpdateTimestamp)})</span>
            </p>
          </div>
        </div>
      </section>
    </article>
  )
}
